import {sourceMediaRequest} from '@waka/compat/media-request';
import React, { useMemo } from 'react';
import { OffthreadVideo, useRemotionEnvironment } from 'remotion';
import { BrowserRenderMediaVideo as MediaVideo } from '@waka/compat/browser-render-media';
import {BrowserAlphaVideo} from '@waka/compat/browser-alpha-video';
import {assertAlphaReframeMetadata,reframeCropMuted,reframeVideoStyle} from '@waka/compat/alpha-reframe';
import { RequireCachedAsset } from '../../caching/require-cached-asset';
import { useAssetFromItem } from '../../utils/use-context';
import { VideoItem } from './video-item-type';
import type { CropRect, ReframeCropResult } from './reframe-utils';

interface ReframeVideoRendererProps {
  item: VideoItem;
  reframeCrop: ReframeCropResult;
  src: string;
  volume: (frame: number) => number;
  startFrom: number;
  trackMuted: boolean;
  isInExtensionArea: boolean;
  compositionWidth: number;
  compositionHeight: number;
  isBlob: boolean;
  transparent?: boolean;
  declaredPixels: number;
  hasAudioTrack: boolean;
  /** ProRes 중간본 등 @remotion/media 비대응 코덱: 처음부터 OffthreadVideo 강제 */
  forceOffthread?: boolean;
  playbackRate: number;
  /** 원본 비디오 가로 해상도 (px) */
  sourceVideoWidth: number;
  /** 원본 비디오 세로 해상도 (px) */
  sourceVideoHeight: number;
  disableBoundaryMediaPause?: boolean;
}

/**
 * 단일 crop 영역에 비디오를 렌더링하는 내부 컴포넌트.
 * crop(0~1 정규화)를 사용하여 비디오를 확대/오프셋한다.
 */
const CroppedVideo = ({
  crop,
  containerWidth,
  containerHeight,
  sourceVideoWidth,
  sourceVideoHeight,
  src,
  volume,
  startFrom,
  muted,
  playbackRate,
  isBlob,
  transparent,
  declaredPixels,
  hasAudioTrack,
  forceOffthread,
  disableBoundaryMediaPause,
}: {
  crop: CropRect;
  containerWidth: number;
  containerHeight: number;
  sourceVideoWidth: number;
  sourceVideoHeight: number;
  src: string;
  volume: (frame: number) => number;
  startFrom: number;
  muted: boolean;
  playbackRate: number;
  isBlob: boolean;
  transparent?: boolean;
  declaredPixels: number;
  hasAudioTrack: boolean;
  forceOffthread?: boolean;
  disableBoundaryMediaPause?: boolean;
}) => {
  const environment = useRemotionEnvironment();
  const browserAlpha = Boolean(environment.isClientSideRendering && transparent && !forceOffthread);
  const style = useMemo<React.CSSProperties>(() => reframeVideoStyle(
    crop,
    containerWidth,
    containerHeight,
    sourceVideoWidth,
    sourceVideoHeight,
    browserAlpha,
  ), [crop, containerWidth, containerHeight, sourceVideoWidth, sourceVideoHeight, browserAlpha]);

  if (browserAlpha) {
    return <BrowserAlphaVideo src={src} style={style} trimBefore={startFrom}
      playbackRate={playbackRate} muted={muted} hasAudioTrack={hasAudioTrack}
      declaredPixels={declaredPixels} volume={volume}/>;
  }

  if (!environment.isClientSideRendering && (isBlob || transparent || forceOffthread)) {
    return (
      <OffthreadVideo
        pauseWhenBuffering={!disableBoundaryMediaPause}
        crossOrigin={'anonymous' as const}
        useWebAudioApi
        volume={volume}
        trimBefore={startFrom}
        src={src}
        transparent={forceOffthread ? false : transparent || undefined}
        toneMapped={forceOffthread ? false : undefined}
        style={style}
        muted={muted}
        playbackRate={playbackRate}
      />
    );
  }

  return (
    <MediaVideo
      requestInit={sourceMediaRequest}
      volume={volume}
      trimBefore={startFrom}
      src={src}
      style={style}
      muted={muted}
      playbackRate={playbackRate}
    />
  );
};

/**
 * 리프레임 비디오 렌더러.
 * single 모드: 전체 화면에 하나의 crop
 * dual 모드: 상/하 분할로 두 개의 crop
 */
export const ReframeVideoRenderer = ({
  item,
  reframeCrop,
  src,
  volume,
  startFrom,
  trackMuted,
  isInExtensionArea,
  compositionWidth,
  compositionHeight,
  isBlob,
  transparent,
  declaredPixels,
  hasAudioTrack,
  forceOffthread,
  playbackRate,
  sourceVideoWidth,
  sourceVideoHeight,
  disableBoundaryMediaPause,
}: ReframeVideoRendererProps) => {
  const asset = useAssetFromItem(item);
  const environment = useRemotionEnvironment();
  const browserAlpha = Boolean(environment.isClientSideRendering && transparent && !forceOffthread);
  // Reframe metadata is immutable in the saved document. Its validation cost
  // must not multiply by output frames or by the two crop children.
  useMemo(() => {
    if (browserAlpha) assertAlphaReframeMetadata(item.reframeMeta, sourceVideoWidth, sourceVideoHeight);
  }, [browserAlpha, item.reframeMeta, sourceVideoWidth, sourceVideoHeight]);
  const muted = trackMuted || isInExtensionArea || item.muteAudio === true;

  const outerStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'relative',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    }),
    [],
  );

  const halfHeight = compositionHeight / 2;

  const topStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'absolute',
      left: 0,
      top: 0,
      width: compositionWidth,
      height: halfHeight,
      overflow: 'hidden',
    }),
    [compositionWidth, halfHeight],
  );

  const bottomStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'absolute',
      left: 0,
      top: halfHeight,
      width: compositionWidth,
      height: halfHeight,
      overflow: 'hidden',
    }),
    [compositionWidth, halfHeight],
  );

  if (reframeCrop.mode === 'dual') {
    return (
      <div style={outerStyle}>
        <RequireCachedAsset asset={asset}>
          {/* 상단: 첫 번째 track — 오디오 재생 */}
          <div style={topStyle}>
            <CroppedVideo
              crop={reframeCrop.topCrop}
              containerWidth={compositionWidth}
              containerHeight={halfHeight}
              sourceVideoWidth={sourceVideoWidth}
              sourceVideoHeight={sourceVideoHeight}
              src={src}
              volume={volume}
              startFrom={startFrom}
              muted={reframeCropMuted(muted, 0)}
              playbackRate={playbackRate}
              isBlob={isBlob}
              transparent={transparent}
              declaredPixels={declaredPixels}
              hasAudioTrack={hasAudioTrack}
              forceOffthread={forceOffthread}
              disableBoundaryMediaPause={disableBoundaryMediaPause}
            />
          </div>
          {/* 하단: 두 번째 track — 오디오 음소거 (중복 방지) */}
          <div style={bottomStyle}>
            <CroppedVideo
              crop={reframeCrop.bottomCrop}
              containerWidth={compositionWidth}
              containerHeight={halfHeight}
              sourceVideoWidth={sourceVideoWidth}
              sourceVideoHeight={sourceVideoHeight}
              src={src}
              volume={volume}
              startFrom={startFrom}
              muted={reframeCropMuted(muted, 1)}
              playbackRate={playbackRate}
              isBlob={isBlob}
              transparent={transparent}
              declaredPixels={declaredPixels}
              hasAudioTrack={hasAudioTrack}
              forceOffthread={forceOffthread}
              disableBoundaryMediaPause={disableBoundaryMediaPause}
            />
          </div>
        </RequireCachedAsset>
      </div>
    );
  }

  // single 모드
  return (
    <div style={outerStyle}>
      <RequireCachedAsset asset={asset}>
        <CroppedVideo
          crop={reframeCrop.crop}
          containerWidth={compositionWidth}
          containerHeight={compositionHeight}
          sourceVideoWidth={sourceVideoWidth}
          sourceVideoHeight={sourceVideoHeight}
          src={src}
          volume={volume}
          startFrom={startFrom}
          muted={reframeCropMuted(muted, 0)}
          playbackRate={playbackRate}
          isBlob={isBlob}
          transparent={transparent}
          declaredPixels={declaredPixels}
          hasAudioTrack={hasAudioTrack}
          forceOffthread={forceOffthread}
          disableBoundaryMediaPause={disableBoundaryMediaPause}
        />
      </RequireCachedAsset>
    </div>
  );
};
